import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BsDatepickerModule,
  BsDatepickerConfig,
} from 'ngx-bootstrap/datepicker';
import { Country } from 'country-state-city';

import { UserService } from '../../../services/user.service';
import { ProjectAndAdminInfo } from '../../../models/project-and-admin-info.model';
import { environment } from '../../../../environments/environment';
import { TrimTextInputDirective } from '../../../directives/trim-text-input.directive';
import { ProjectAutoFetchService } from '../../../services/project-auto-fetch.service';

@Component({
  selector: 'app-edit-project',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BsDatepickerModule,
    TrimTextInputDirective,
  ],
  templateUrl: './edit-project.component.html',
  styleUrl: './edit-project.component.css',
})
export class EditProjectComponent implements OnInit {
  selectedFile: File | null = null;
  fileError: string | null = null;
  selectedFileName: string | null = null;
  projectId: string | null = null;
  project: ProjectAndAdminInfo = new ProjectAndAdminInfo();
  projectAdminId: string | null = null;
  datePickerConfig: Partial<BsDatepickerConfig>;
  defaultCountryCode: String = environment.DefaultCountryCode;
  countryCodes: Array<{ code: string; name: string; dial_code: string }> = [];

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  constructor(
    private userService: UserService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private projectAutoFetchService: ProjectAutoFetchService
  ) {
    this.datePickerConfig = {
      maxDate: new Date(),
      dateInputFormat: 'DD/MM/YYYY',
    };
  }
  ngOnInit(): void {
    this.spinner.show();
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.loadCountryCodes();
    this.getProjectDetails();
  }

  // Trigger the file input when the user clicks on the text input
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    // Clear previous errors
    this.fileError = null;

    if (file) {
      // Check if the file is an image
      if (!file.type.startsWith('image/')) {
        this.fileError = 'Please select a valid image file (jpg, png, etc.)';
        this.selectedFile = null;
        this.selectedFileName = null;
      } else {
        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    }
  }

  getProjectDetails(): void {
    // TODO: Fetch project details based on ID
    this.userService.getProjectDetails(this.projectId).subscribe(
      (response: any) => {
        // Populate form with project details
        this.project = response.data;
        this.projectAdminId = response.data?.project_manager_id;
        this.project.contact_info =
          response.data?.ProjectManager?.contact_info ?? '';
        this.project.email = response.data?.ProjectManager?.email ?? '';
        this.project.firstname = response.data?.ProjectManager?.firstname ?? '';
        this.project.gender = response.data?.ProjectManager?.gender ?? '';
        this.project.lastname = response.data?.ProjectManager?.lastname ?? '';
        const [country_code, phone_no] =
          response.data?.ProjectManager?.phone_no.split(',');
        this.defaultCountryCode = country_code;
        this.project.phone_no = phone_no ?? country_code ?? '';
        this.project.profile_image_actual_filename =
          response.data?.ProjectManager?.profile_image_actual_filename ?? '';
        this.project.profile_image_filename =
          response.data?.ProjectManager?.profile_image_filename ?? '';
        this.project.username = response.data?.ProjectManager?.username ?? '';
        this.selectedFileName =
          this.project.profile_image_actual_filename.toString();
        //this.project.dob = this.project.dob?.toISOString().split('T')[0];
        if (response.data?.ProjectManager?.dob) {
          /*this.project.dob = new Date(response.data?.ProjectManager?.dob)
            .toISOString()
            .split('T')[0];*/
          this.project.dob = this.getformatDateForInput(
            new Date(response.data?.ProjectManager?.dob)
          );
        }
        this.spinner.hide();
      },
      (error: any) => {
        this.toastr.error(error.error.message.Text, 'Edit Project');
        this.spinner.hide();
        console.error(error.error.message);
      }
    );
  }

  onSubmit(frm: NgForm): void {
    if (this.fileError === null) {
      this.spinner.show();
      let frmInput = frm.value;

      const formData = new FormData();

      if (frmInput.dob) {
        let birthDate = this.setformatDateForInput(frmInput.dob);
        formData.append('dob', birthDate);
      }

      let phoneNumber = `${frmInput.country_code},${frmInput.phone_no}`;

      formData.append('company_name', frmInput.company_name.trim());
      formData.append('contact_info', frmInput.contact_info);
      formData.append('email', frmInput.email);
      formData.append('firstname', frmInput.firstname.trim());
      formData.append('gender', frmInput.gender);
      formData.append('lastname', frmInput.lastname.trim());
      formData.append('name', frmInput.name.trim());
      formData.append('password', frmInput.password);
      formData.append('phone_no', phoneNumber);
      formData.append('username', frmInput.username.trim());
      // Add profile image if selected
      if (this.selectedFile) {
        formData.append('profile_image', this.selectedFile);
      }
      if (this.projectId) {
        formData.append('project_id', this.projectId);
      }
      if (this.projectAdminId) {
        formData.append('project_manager_id', this.projectAdminId);
      }

      this.userService.editProject(formData).subscribe(
        (response: any) => {
          this.projectAutoFetchService.setProject({
            project_id: this.projectId,
            name: frmInput.name.trim(),
          });
          this.spinner.hide();
          this.toastr.success(response.message.Text, 'Edit Project');
          this.router.navigateByUrl('/project/list');
        },
        (error: any) => {
          this.toastr.error(error.error.message.Text, 'Edit Project');
          this.spinner.hide();
          console.error(error.error.message);
        }
      );
    }
  }

  oncancel = (): void => {
    this.router.navigateByUrl('/project/list');
  };

  preventInput(event: KeyboardEvent): void {
    event.preventDefault();
  }

  loadCountryCodes() {
    const countryData = Country.getAllCountries();
    this.countryCodes = countryData.map((country) => ({
      name: country.name,
      dial_code: country.phonecode,
      code: country.isoCode,
    }));
  }

  private setformatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getformatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  }
}
